import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent, DialogConfig } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-create-email-template-dialog',
  templateUrl: './create-email-template.dialog.html',
  standalone: false,
})
export class CreateEmailTemplateDialog {
  private readonly emailValidatorControl = new FormControl('', [Validators.email]);

  protected readonly deliveryModes = [
    { value: Policy.DeliveryMode.EVENT_DRIVEN, label: 'Event Based' },
    { value: Policy.DeliveryMode.SCHEDULED, label: 'Schedule Based' },
  ];
  protected readonly scheduleAnchors = [
    { value: Policy.ScheduleAnchor.EXPIRY_DATE, label: 'Expiry Date' },
    { value: Policy.ScheduleAnchor.TERM_START_DATE, label: 'Term Start Date' },
  ];
  protected readonly eventTypeGroups: { label: string; options: Policy.EmailType[] }[] = [
    {
      label: 'Policy',
      options: [
        Policy.EmailType.warrantyProvisioned,
        Policy.EmailType.warrantyRenewal,
        Policy.EmailType.renewalReminder,
        Policy.EmailType.loginLink,
      ],
    },
    {
      label: 'Claim',
      options: [
        Policy.EmailType.claimCreated,
        Policy.EmailType.claimClosed,
        Policy.EmailType.claimPaymentIssued,
        Policy.EmailType.claimDocumentUploaded,
      ],
    },
  ];
  protected readonly emailTypes = this.eventTypeGroups.flatMap(group => group.options);
  protected readonly systemAttachmentOptions = [
    { value: Policy.PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS, label: 'Policy Terms & Conditions' },
    { value: Policy.PolicyEmailSystemAttachment.COVERAGE_SUMMARY, label: 'Policy Coverage Summary' },
    { value: Policy.PolicyEmailSystemAttachment.REGISTRATION_PAGE, label: 'Warranty Registration Page' },
  ];

  @ViewChild(DialogComponent) dialog!: DialogComponent;
  @Input() dialogConfig?: DialogConfig;
  @Input() policy?: Policy.Model;

  saving = false;
  offsetInput = '';
  recipientInput = '';
  offsetError = '';
  recipientError = '';

  form = this.formBuilder.group({
    type: [this.emailTypes[0] ?? null, Validators.required],
    templateLabel: ['', Validators.required],
    deliveryMode: [Policy.DeliveryMode.EVENT_DRIVEN, Validators.required],
    subject: [''],
    byline: [''],
    content: ['', []],
    scheduleAnchor: [Policy.ScheduleAnchor.EXPIRY_DATE as Policy.ScheduleAnchor | null],
    scheduleOffsetsDays: [[] as number[]],
    additionalRecipients: [[] as string[]],
    systemAttachments: [[Policy.PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS]],
    newAttachments: [[] as File[]],
  });

  constructor(
    private formBuilder: FormBuilder,
    private policySvc: PolicyService,
    private toastSvc: ToastService
  ) {}

  get isScheduledTemplate() {
    return this.form.controls.deliveryMode.value === Policy.DeliveryMode.SCHEDULED;
  }

  get scheduleOffsetsDays() {
    return this.form.controls.scheduleOffsetsDays.value ?? [];
  }

  get additionalRecipients() {
    return this.form.controls.additionalRecipients.value ?? [];
  }

  get isWarrantyProvisionedTemplate() {
    return (
      this.form.controls.type.value === Policy.EmailType.warrantyProvisioned &&
      this.form.controls.deliveryMode.value === Policy.DeliveryMode.EVENT_DRIVEN
    );
  }

  get systemAttachments() {
    return this.form.controls.systemAttachments.value ?? [];
  }

  get newAttachments() {
    const value = this.form.controls.newAttachments.value;
    return Array.isArray(value) ? value : value ? [value] : [];
  }

  open() {
    this.resetForm();
    this.dialog.open();
  }

  close() {
    this.dialog.close();
  }

  resetForm() {
    this.form.reset({
      type: this.emailTypes[0] ?? null,
      templateLabel: '',
      deliveryMode: Policy.DeliveryMode.EVENT_DRIVEN,
      subject: '',
      byline: '',
      content: '',
      scheduleAnchor: Policy.ScheduleAnchor.EXPIRY_DATE,
      scheduleOffsetsDays: [],
      additionalRecipients: [],
      systemAttachments: [Policy.PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS],
      newAttachments: [],
    });
    this.offsetInput = '';
    this.recipientInput = '';
    this.offsetError = '';
    this.recipientError = '';
    this.form.markAsPristine();
  }

  cancel() {
    this.resetForm();
    this.close();
  }

  async submit() {
    if (!this.policy || this.saving) return;
    this.saving = true;

    if (this.isScheduledTemplate) this.addOffsetFromInput();
    this.addRecipientsFromInput();
    if (
      (this.isScheduledTemplate && this.offsetError) ||
      this.recipientError ||
      this.hasInvalidAdditionalRecipients()
    ) {
      this.saving = false;
      return;
    }

    const uploadedAttachments: Policy.PolicyEmailUploadedAttachment[] = [];
    try {
      if (this.isWarrantyProvisionedTemplate) {
        for (const file of this.newAttachments) {
          uploadedAttachments.push(await this.policySvc.uploadEmailAttachment(this.policy.id, file));
        }
      }

      const dto: Policy.CreatePolicyEmailRequest = {
        policyId: this.policy.id,
        type: this.form.controls.type.value as Policy.EmailType,
        templateLabel: (this.form.controls.templateLabel.value ?? '').trim(),
        subject: this.form.controls.subject.value ?? null,
        byline: this.form.controls.byline.value ?? null,
        content: this.form.controls.content.value ?? null,
        instructionImage: null,
        deliveryMode: this.form.controls.deliveryMode.value,
        scheduleAnchor: this.isScheduledTemplate ? (this.form.controls.scheduleAnchor.value ?? null) : null,
        scheduleOffsetsDays: this.isScheduledTemplate ? this.normalizeOffsets(this.scheduleOffsetsDays) : [],
        additionalRecipients: this.normalizeRecipients(this.additionalRecipients),
        ...(this.isWarrantyProvisionedTemplate && {
          systemAttachments: this.systemAttachments,
          uploadedAttachments,
        }),
      };

      const created = await this.policySvc.createEmail(this.policy.id, dto);
      if (!created?.id) throw new Error('The email template could not be created');

      this.toastSvc.show({
        type: 'success',
        title: 'Created email template',
        text: 'The template has been successfully created.',
      });
      this.close();
      this.policySvc.refreshPolicy();
    } catch (error) {
      await this.cleanupUploads(uploadedAttachments);
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: this.getErrorMessage(error),
      });
    } finally {
      this.saving = false;
    }
  }

  hasSystemAttachment(type: Policy.PolicyEmailSystemAttachment) {
    return this.systemAttachments.includes(type);
  }

  toggleSystemAttachment(type: Policy.PolicyEmailSystemAttachment) {
    const next = this.hasSystemAttachment(type)
      ? this.systemAttachments.filter(value => value !== type)
      : [...this.systemAttachments, type];
    this.form.controls.systemAttachments.setValue(next);
    this.form.controls.systemAttachments.markAsDirty();
  }

  addOffsetFromInput() {
    const value = this.offsetInput.trim();
    if (!value) return;
    if (!/^[-+]?\d+$/.test(value)) {
      this.offsetError = 'Offset must be a whole number (e.g. -7, 0, +15).';
      return;
    }
    this.offsetError = '';
    this.form.controls.scheduleOffsetsDays.setValue(
      this.normalizeOffsets([...this.scheduleOffsetsDays, Number(value)])
    );
    this.form.controls.scheduleOffsetsDays.markAsDirty();
    this.offsetInput = '';
  }

  removeOffset(offset: number) {
    this.form.controls.scheduleOffsetsDays.setValue(this.scheduleOffsetsDays.filter(item => item !== offset));
    this.form.controls.scheduleOffsetsDays.markAsDirty();
  }

  addRecipientsFromInput() {
    const rawValue = this.recipientInput.trim();
    this.recipientError = '';
    if (!rawValue) return;
    const entries = rawValue
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    const invalid = entries.find(value => !this.isValidEmail(value));
    if (invalid) {
      this.recipientError = `Invalid email address: ${invalid}`;
      return;
    }
    this.recipientError = '';
    this.form.controls.additionalRecipients.setValue(
      this.normalizeRecipients([...this.additionalRecipients, ...entries])
    );
    this.form.controls.additionalRecipients.markAsDirty();
    this.recipientInput = '';
  }

  removeRecipient(recipient: string) {
    this.form.controls.additionalRecipients.setValue(this.additionalRecipients.filter(item => item !== recipient));
    this.form.controls.additionalRecipients.markAsDirty();
  }

  private hasInvalidAdditionalRecipients() {
    return this.additionalRecipients.some(value => !this.isValidEmail(value));
  }

  private isValidEmail(value: string) {
    this.emailValidatorControl.setValue(value);
    return !this.emailValidatorControl.hasError('email');
  }

  private normalizeOffsets(offsets: number[]) {
    const deduped = Array.from(new Set(offsets.map(value => Number(value)).filter(value => Number.isInteger(value))));
    return deduped.sort((a, b) => a - b);
  }

  private normalizeRecipients(recipients: string[]) {
    return Array.from(new Set(recipients.map(value => value.trim().toLowerCase()).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
  }

  private async cleanupUploads(attachments: Policy.PolicyEmailUploadedAttachment[]) {
    const policyId = this.policy?.id;
    if (!policyId) return;
    await Promise.all(
      attachments.map(attachment =>
        this.policySvc.deleteEmailAttachment(policyId, attachment.storageItemId).catch(() => undefined)
      )
    );
  }

  private getErrorMessage(error: unknown) {
    const response = error as { error?: { message?: string } | string; message?: string };
    if (typeof response?.error === 'string') return response.error;
    return response?.error?.message ?? response?.message ?? 'Unable to save the email template.';
  }
}
